<template>
  <div>
    <Accordion :is-open="false">
      <template #head>
        さらに絞り込む
      </template>
      <template #body>
        <div class="boss-table-2">
          <scout-form-gender
            v-if="showGender"
            v-model="filterData.gender"
          />
          <scout-form-prefecture
            v-if="showPrefecture"
            v-model="filterData.presentAddressPrefectures"
            label="現住所都道府県"
          />
          <scout-form-educational-background
            v-if="showEducationalBackground"
            v-model="filterData.educationalBackground"
          />
          <scout-form-employed
            v-if="showEmployed"
            v-model="filterData.employed"
          />
          <scout-form-others v-bind.sync="filterData" />
          <scout-form-member-id v-model.number="filterData.userId" />
          <scout-form-pull-down
            v-if="!isRecommended && !isSearch"
            v-model="filterData.searchConditionName"
            :options="searchConditionNameOptions"
            data-test="search-name"
            label="検索条件名"
          />
        </div>
      </template>
    </Accordion>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import Accordion from '@/components/atoms/Accordion'
import ScoutFormEmployed from '@/components/atoms/ScoutFormEmployed'
import ScoutFormGender from '@/components/atoms/ScoutFormGender'
import ScoutFormMemberId from '@/components/atoms/ScoutFormMemberId'
import ScoutFormPrefecture from '@/components/atoms/ScoutFormPrefecture'
import ScoutFormPullDown from '@/components/atoms/ScoutFormPullDown'
import ScoutFormEducationalBackground from '@/components/atoms/ScoutFormEducationalBackground'
import ScoutFormOthers from '@/components/atoms/ScoutFormOthers'

const defaultFilterData = {
  gender: null,
  educationalBackground: null,
  employed: null,
  hasCareerResumes: false,
  presentAddressPrefectures: [],
  excludesDisabledScout: undefined,
  userId: null,
  searchConditionName: '',
  excludesScouted: undefined,
  excludesLiked: undefined
}

export default {
  components: {
    Accordion,
    ScoutFormEmployed,
    ScoutFormGender,
    ScoutFormMemberId,
    ScoutFormPrefecture,
    ScoutFormPullDown,
    ScoutFormEducationalBackground,
    ScoutFormOthers
  },
  props: {
    memberObjects: {
      type: Array,
      default: () => ([])
    },
    isCandidate: {
      type: Boolean,
      default: false
    },
    isLiked: {
      type: Boolean,
      default: false
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    isRecommended: {
      type: Boolean,
      default: false
    },
    jobId: {
      type: Number,
      default: null
    },
    searchParameter: {
      type: Object,
      default: () => ({
        gender: null,
        educationalBackground: null,
        employed: null,
        hasCareerResumes: false,
        address: {
          prefectures: [],
          selectedOption: null
        }
      })
    }
  },
  data () {
    return {
      filterData: _.cloneDeep(defaultFilterData),
      filterMemberObjects: [],
      renderModal: true
    }
  },
  computed: {
    ...mapGetters({
      prefecturesMaster: 'prefecturesMaster'
    }),
    ...mapGetters({
      searchDataByJobId: 'membersSearchCondition'
    }),
    searchConditionNameOptions () {
      return _(this.memberObjects).map('member').map('searchConditionName').uniq().compact().map(v => {
        return { id: v, name: v }
      }).value()
    },
    searchDataParameter () {
      return this.searchDataByJobId(this.jobId).parameter
    },
    showGender () {
      return this.searchParameter.gender === null
    },
    showPrefecture () {
      return this.renderModal &&
        (this.searchParameter.address.prefectures.length === 0 || this.searchParameter.address.selectedOption !== 'present')
    },
    showEducationalBackground () {
      return this.searchParameter.educationalBackground === null
    },
    showEmployed () {
      return this.searchParameter.employed === null
    }
  },
  watch: {
    filterData: {
      handler () {
        this.filter()
      },
      deep: true
    },
    'memberObjects' () {
      this.filter()
    },
    searchDataParameter: {
      handler () {
        this.fetchSearchParameter()
      }
    }
  },
  created () {
    const masters = [
      'genders',
      'educationalBackgrounds',
      'prefectures'
    ]
    masters.forEach(element => {
      this.storeMaster(element)
    })
    this.$set(this.filterData, 'excludesDisabledScout', (this.isCandidate) ? false : undefined)
    this.$set(this.filterData, 'excludesScouted', (this.isLiked) ? false : undefined)
    this.$set(this.filterData, 'excludesLiked', ((this.isSearch || this.isCandidate) ? false : undefined))
    this.fetchSearchParameter()
  },
  methods: {
    ...mapActions({
      storeMaster: 'storeMaster'
    }),
    isDisabled (val) {
      // 0やブランクやnullをtrueで返すようなよさげなメソッドないかな
      return !!val
    },
    filter () {
      let filterMemberObjects = this.memberObjects
      if (this.filterData.gender) {
        filterMemberObjects = filterMemberObjects.filter(memberObject => _.get(memberObject, 'member.gender.id') === this.filterData.gender)
      }
      if (this.filterData.educationalBackground) {
        filterMemberObjects = filterMemberObjects
          .filter(memberObject => memberObject.member.educationalBackground && memberObject.member.educationalBackground.id <= this.filterData.educationalBackground)
      }
      if (this.filterData.employed !== null) {
        filterMemberObjects = filterMemberObjects.filter(memberObject => memberObject.member.employed === this.filterData.employed)
      }
      if (this.filterData.hasCareerResumes) {
        filterMemberObjects = filterMemberObjects.filter(memberObject => !_.isEmpty(memberObject.member.careerResume))
      }
      if (this.filterData.presentAddressPrefectures.length) {
        filterMemberObjects = filterMemberObjects
          .filter(memberObject => memberObject.member.presentAddressPrefecture && this.filterData.presentAddressPrefectures.some(presentAddressPrefectureId => presentAddressPrefectureId === memberObject.member.presentAddressPrefecture.id))
      }
      if ((this.isCandidate || this.isLiked) && this.filterData.excludesDisabledScout) {
        filterMemberObjects = filterMemberObjects.filter(memberObject => !memberObject.member.isWithdrawn && memberObject.member.subscriptions.scout &&
          !memberObject.member.isEntry && !memberObject.member.isScoutRejection &&
          !memberObject.member.isCkUser && !memberObject.member.isScoutExcluded)
      }
      if (this.filterData.userId) {
        filterMemberObjects = _.filter(filterMemberObjects, o => {
          return String(o.member.id).match('^' + this.filterData.userId)
        })
      }
      if (this.filterData.searchConditionName) {
        filterMemberObjects = _.filter(filterMemberObjects, o => {
          return this.filterData.searchConditionName === o.member.searchConditionName
        })
      }
      if (this.isLiked && this.filterData.excludesScouted) {
        filterMemberObjects = filterMemberObjects.filter(memberObject => !memberObject.member.isWithdrawn && memberObject.member.subscriptions.scout &&
          !memberObject.member.isEntry && !memberObject.member.isScoutRejection &&
          !memberObject.member.isCkUser && !memberObject.member.isScoutExcluded && !memberObject.member.scouted && !memberObject.member.isScoutReserved)
      }
      if (this.filterData.excludesLiked) {
        filterMemberObjects = _.filter(filterMemberObjects, o => {
          return !o.member.liked
        })
      }
      this.$set(this, 'filterMemberObjects', filterMemberObjects)
      this.$emit('setMemberObjects', this.filterMemberObjects)
    },
    clearFilterData () {
      this.$set(this, 'filterData', _.cloneDeep(defaultFilterData))
      this.$set(this.filterData, 'excludesDisabledScout', (this.isCandidate) ? false : undefined)
      this.$set(this.filterData, 'excludesScouted', (this.isLiked) ? false : undefined)
      this.$set(this.filterData, 'excludesLiked', ((this.isSearch || this.isCandidate) ? false : undefined))
      this.rerender()
    },
    rerender () {
      // モーダル初期化のため
      this.$set(this, 'renderModal', false)
      this.$nextTick(() => {
        this.$set(this, 'renderModal', true)
      })
    },
    fetchSearchParameter () {
      this.$set(this.searchParameter, 'gender', (this.isSearch) ? this.searchDataParameter.gender : null)
      this.$set(this.searchParameter, 'educationalBackground', (this.isSearch) ? this.searchDataParameter.educationalBackground : null)
      this.$set(this.searchParameter, 'employed', (this.isSearch) ? this.searchDataParameter.employed : null)
      this.$set(this.searchParameter.address, 'prefectures', (this.isSearch) ? this.searchDataParameter.address.prefectures : [])
      this.$set(this.searchParameter.address, 'selectedOption', (this.isSearch) ? this.searchDataParameter.address.selectedOption : null)
    }
  }
}
</script>
